import WAFRIcon from "../../assets/images/aws/AWS-Well-Architected-Tool.png";
import { isMarginManagementUIVisible, isMSP, isMSPUser, isCustomerSummaryDashboardVisible } from "../../helpers/util_helper";

// Path prefixes allowed when Azure is selected — mirrors Azure sidebar enablement
// (Dashboard, SecOps CSPM + IaC, Compliance, Configuration, Support).
const AZURE_ENABLED_PATH_PREFIXES = [
  "/dashboard",
  "/cspm",
  "/iacmanagement",
  "/compliance",
  "/configuration",
  "/supportrequest",
  "/releasenotes",
  "/user-manual",
  "/faq",
];

export const isRouteDisabledForAzure = (pathname = "") => {
  const path = pathname.toLowerCase();
  return !AZURE_ENABLED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

export const sidebarMenuOptions = (t, handleRedirectLink, profile, licenseData, isAzureView = false) => {
  const isAdmin =
    profile?.userRoles?.some((role) => role === "Administrator") || false;
  const isPowerUser =
    profile?.userRoles?.some((role) => role === "PowerUser") || false;

  const isMarginManagementVisible = isMarginManagementUIVisible();

  // Check if license is CUSTOMER - if so, disable all items except Dashboard and FinOps
  const isCustomerLicense = licenseData?.data?.licenseName === "CUSTOMER";

  // Always build and return the menu so the sidebar is never blank (e.g. while profile is loading).
  // Role-based visibility will update when profile loads.

  // Helper function to determine if a menu item should be disabled
  const shouldDisableItem = (item) => {
    if (!isCustomerLicense) return false;
    // Keep Dashboard enabled
    if (item.link === "/dashboard" || item.group === "Dashboard") return false;
    // Keep FinOps and all its submenu items enabled
    if (item.group === "cloudSpend" || item.group === "costInsight" || 
        item.group === "SpendAllocation" || item.group === "SpendCategorization" ||
        item.group === "costOptimization" ||
        item.group === "MarginManagement" || item.group === "VendorInsight") return false;
    return true;
  };

  // Azure view: enable only Dashboard, SecOps (CSPM + IaC Scanning), Compliance,
  // Configuration and Support. Everything else is disabled (greyed out).
  // Keep in sync with isRouteDisabledForAzure below.
  const AZURE_ENABLED_LINKS = ["/dashboard", "/cspm", "/iacmanagement/dashboard", "/compliance/"];
  const AZURE_ENABLED_GROUPS = ["secOps", "configuration", "Support"];
  const shouldDisableForAzure = (item) => {
    if (!isAzureView) return false;
    if (item.link && AZURE_ENABLED_LINKS.includes(item.link)) return false;
    if (item.group && AZURE_ENABLED_GROUPS.includes(item.group)) return false;
    return true;
  };

  return [
    {
      id: 17,
      link: "/dashboard",
      clickHandler: handleRedirectLink,
      group: "Dashboard",
      classNames: "waves-effect",
      iconClassName: "ri-dashboard-fill",
      displayName: t("Dashboard"),
      isVisible: true,
      isActive: false,
    },
    {
      id: 171,
      link: "/summary",
      clickHandler: handleRedirectLink,
      group: "Dashboard",
      classNames: "waves-effect",
      iconClassName: "ri-file-list-3-line",
      displayName: t("ARN Insight"),
      isVisible: true,
      isActive: false,
      isDisabled: shouldDisableItem({ link: "/summary", group: "Dashboard" }) || shouldDisableForAzure({ link: "/summary", group: "Dashboard" }),
    },
    {
      id: 0,
      link: "/assetinsight",
      clickHandler: handleRedirectLink,
      group: "ApplicationIntegration",
      classNames: "waves-effect",
      iconClassName: "ri-file-list-3-fill",
      displayName: t("Asset Insight"),
      isVisible: true,
      expand: false,
      isActive: false,
      isDisabled: shouldDisableItem({ link: "/assetinsight", group: "ApplicationIntegration" }) || shouldDisableForAzure({ link: "/assetinsight", group: "ApplicationIntegration" }),
    },
    {
      id: 14,
      link: "#",
      group: "cloudSpend",
      clickHandler: handleRedirectLink,
      classNames: "waves-effect",
      iconClassName: "ri-money-dollar-circle-fill",
      displayName: t("FinOps"),
      isVisible: true,
      expand: false,
      isActive: false,
      isDisabled: shouldDisableForAzure({ link: "#", group: "cloudSpend" }),
      subMenu: [
        // {
        //   id: 20,
        //   link: "/finops",
        //   group: "FinOps",
        //   clickHandler: handleRedirectLink,
        //   classNames: "waves-effect",
        //   displayName: t("Customer Summary"),
        //   isVisible: isCustomerSummaryDashboardVisible(),
        //   isActive: false,
        //   isSubmenu: true,
        // },
        {
          id: 14,
          link: "/costinsight",
          group: "costInsight",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Cloud Spend"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
        },
        {
          id: 14,
          link: "/SpendAllocation",
          group: "SpendAllocation",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Workload Spend"),
          isVisible: !isCustomerLicense,
          isActive: false,
          isSubmenu: true,
        },
        {
          id: 17,
          link: "/spend-categorization",
          group: "SpendCategorization",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Budget Tracking"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
        },
        {
          id: 16,
          link: "/customer-management",
          group: "MarginManagement",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Customer Management"),
          isVisible: isMarginManagementVisible,
          isActive: false,
          isSubmenu: true,
        },
        {
          id: 14,
          link: "/vendorInsight",
          group: "VendorInsight",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Marketplace Seller Insights"),
          isVisible: isMSPUser() && !isCustomerLicense,
          isActive: false,
          isSubmenu: true,
        },
        {
          id: 18,
          link: "/billing-management",
          group: "BillingManagement",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Billing Management"),
          isVisible: isMarginManagementVisible,
          isActive: false,
          isSubmenu: true,
        },
        {
          id: 15,
          link: "/costoptimization",
          group: "costOptimization",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Cost Optimization"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
        },
      ],
    },
    {
      id: 19,
      link: "#",
      clickHandler: handleRedirectLink,
      group: "secOps",
      classNames: "waves-effect",
      iconClassName: "ri-shield-keyhole-line",
      displayName: t("SecOps"),
      isVisible: true,
      expand: false,
      isActive: false,
      subMenu: [
        {
          id: 11,
          link: "/cloudevent",
          clickHandler: handleRedirectLink,
          group: "eventInsight",
          classNames: "waves-effect",
          displayName: t("Cloud Event"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/cloudevent", group: "eventInsight" }) || shouldDisableForAzure({ link: "/cloudevent", group: "eventInsight" }),
        },
        {
          id: 22,
          link: "/cspm",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          group: "CSPM",
          displayName: t("CSPM"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/cspm", group: "CSPM" }),
        },
        {
          id: 24,
          link: "/remediation-hub",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          group: "RemediationHub",
          displayName: t("Remediation Hub"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/remediation-hub", group: "RemediationHub" }) || shouldDisableForAzure({ link: "/remediation-hub", group: "RemediationHub" }),
        },
        {
          id: 17,
          link: "/iacmanagement/dashboard",
          clickHandler: handleRedirectLink,
          group: "IACManagement",
          classNames: "waves-effect",
          displayName: t("IaC Scanning"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/iacmanagement/dashboard", group: "IACManagement" }),
        },
      ],
    },
    {
      id: 23,
      link: "#",
      clickHandler: handleRedirectLink,
      classNames: "waves-effect",
      group: "Compliance",
      iconClassName: "ri-todo-line",
      displayName: t("Compliance"),
      isVisible: true,
      expand: false,
      isActive: false,
      isDisabled: shouldDisableItem({ group: "Compliance" }),
      subMenu: [
        {
          id: 25,
          link: "/compliance/",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          group: "Compliance",
          displayName: t("Dashboard"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/compliance/", group: "Compliance" }),
        },
        {
          id: 26,
          link: "/audit-hub",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          group: "AuditHub",
          displayName: t("Audit Hub"),
          isVisible: true,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/audit-hub", group: "AuditHub" }) || shouldDisableForAzure({ link: "/audit-hub", group: "AuditHub" }),
        },
      ],
    },
    // {
    //   id: 10,
    //   link: "/Security",
    //   clickHandler: handleRedirectLink,
    //   group: "Security",
    //   classNames: "waves-effect",
    //   iconClassName: "ri-shield-keyhole-line",
    //   displayName: t("Security Posture"),
    //   isVisible: true,
    //   isActive: false,
    // },
    // {
    //   id: 12,
    //   link: "/complianceDashboard",
    //   clickHandler: handleRedirectLink,
    //   classNames: "waves-effect",
    //   group: "ComplianceDashboard",
    //   iconClassName: "ri-todo-line",
    //   displayName: t("Compliance"),
    //   isVisible: true,
    //   isActive: false,
    //   tag: "Old",
    //   tagColor: "#d32f2f",
    //   tagBackgroundColor: "#ffebee"
    // },
    {
      id: 11,
      link: "/rules",
      clickHandler: handleRedirectLink,
      group: "alerts",
      classNames: "waves-effect",
      iconClassName: "ri-notification-line",
      displayName: t("Rules & Alerts"),
      isVisible: isAdmin || isPowerUser,
      isActive: false,
      isDisabled: shouldDisableItem({ link: "/rules", group: "alerts" }) || shouldDisableForAzure({ link: "/rules", group: "alerts" }),
    },
    {
      id: 21,
      link: "/waf",
      group: "WAF",
      clickHandler: handleRedirectLink,
      classNames: "waves-effect",
      customIcon: WAFRIcon,
      iconClassName: "ri-notification-line",
      displayName: t("WAFR"),
      isVisible: true,
      expand: false,
      isActive: false,
      isDisabled: shouldDisableItem({ link: "/waf", group: "WAF" }) || shouldDisableForAzure({ link: "/waf", group: "WAF" }),
      subMenu: [
        {
          id: 100,
          link: "/assessments",
          group: "WAF",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Assessments"),
          isVisible: true,
          expand: false,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/assessments", group: "WAF" }),
          subMenu: [
            {
              id: 101,
              link: "/CISWAF/",
              group: "WAF",
              clickHandler: handleRedirectLink,
              classNames: "waves-effect",
              displayName: t("CIS"),
              isVisible: true,
              isActive: false,
              isSubmenu: true,
              isDisabled: shouldDisableItem({ link: "/CISWAF/", group: "WAF" }),
            },
            {
              id: 102,
              link: "/workload-assessment/",
              group: "WAF",
              clickHandler: handleRedirectLink,
              classNames: "waves-effect",
              displayName: t("Workloads"),
              isVisible: true,
              isActive: false,
              isSubmenu: true,
              isDisabled: shouldDisableItem({ link: "/workload-assessment/", group: "WAF" }),
            },
          ],
        },
        {
          id: 100,
          link: "/wafrreports",
          group: "WAF",
          clickHandler: handleRedirectLink,
          classNames: "waves-effect",
          displayName: t("Reports"),
          isVisible: true,
          expand: false,
          isActive: false,
          isSubmenu: true,
          isDisabled: shouldDisableItem({ link: "/wafrreports", group: "WAF" }),
          subMenu: [
            {
              id: 22,
              link: "/reports/",
              group: "WAF",
              clickHandler: handleRedirectLink,
              classNames: "waves-effect",
              displayName: t("WAFR Details"),
              isVisible: true,
              isActive: false,
              isSubmenu: true,
              isDisabled: shouldDisableItem({ link: "/reports/", group: "WAF" }),
            },
          ],
        },
      ],
    },
    {
      id: 16,
      link: "/configuration/accounts",
      clickHandler: handleRedirectLink,
      group: "configuration",
      classNames: "waves-effect",
      iconClassName: "ri-settings-4-line",
      displayName: t("Configuration"),
      isVisible: true,
      isActive: false,
    },
    {
      id: 18,
      link: "/supportrequest",
      clickHandler: handleRedirectLink,
      group: "Support",
      classNames: "waves-effect",
      iconClassName: "ri-apps-2-fill",
      displayName: t("Support"),
      isVisible: true,
      expand: false,
      isActive: false,
      subMenu: [
        {
          id: 181,
          link: "/supportrequest",
          clickHandler: handleRedirectLink,
          group: "Support",
          classNames: "waves-effect",
          iconClassName: "ri-mail-send-line",
          displayName: t("Create Support Case"),
          isVisible: true,
          isSubmenu: true,
          isActive: false,
        },
        {
          id: 182,
          link: "/releasenotes",
          clickHandler: handleRedirectLink,
          group: "Support",
          classNames: "waves-effect",
          iconClassName: "ri-file-list-3-fill",
          displayName: t("Release Notes"),
          isVisible: true,
          isSubmenu: true,
          isActive: false,
        },
        {
          id: 183,
          link: "/user-manual",
          clickHandler: handleRedirectLink,
          group: "Support",
          classNames: "waves-effect",
          iconClassName: "ri-file-list-3-fill",
          displayName: t("User Manual"),
          isVisible: true,
          isSubmenu: true,
          isActive: false,
        },
        {
          id: 183,
          link: "/faq",
          clickHandler: handleRedirectLink,
          group: "Support",
          classNames: "waves-effect",
          iconClassName: "ri-file-list-3-fill",
          displayName: t("FAQ"),
          isVisible: true,
          isSubmenu: true,
          isActive: false,
        },
      ],
    },
  ];
};
